import { CircleEmptyAlertIcon, InfoBanner, useToast } from "@codat/orchard-ui";
import { GetCompaniesResponse, useCreateRuleMutation } from "api/public";
import React from "react";
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  useForm,
} from "react-hook-form";
import { clientRuleTypes } from "utils/constants";

import Button from "components/Button";
import Modal from "components/Modal";
import ModalBody from "components/ModalBody";
import SelectInput from "components/SelectInput";
import TextArea from "components/TextArea";
import TextInput from "components/TextInput";

import { formatEmails, validateEmails, validateWebhookUrl } from "../utils";
import styles from "../WebhookRules.module.css";

interface CreateRuleModalProps {
  onClose: () => void;
  onSuccess: () => void;
  companies: GetCompaniesResponse | undefined;
}

type RuleType =
  | "Data sync completed"
  | "New company synchronised"
  | "DataConnectionStatusChanged"
  | "Dataset data changed"
  | "Data Sync Status Changed To Error"
  | "Push Operation Status Changed()"
  | "Push Operation Timed Out"
  | "account-categories-updated"
  | "sync-connection-deleted"
  | "sync-complete"
  | "sync-failed"
  | "Rate Limit Reached"
  | "Rate Limit Reset";

const ruleTypeOptions: { value: RuleType; label: string }[] = [
  {
    value: "Data sync completed",
    label: "Data sync completed",
  },
  {
    value: "New company synchronised",
    label: "New company synchronised",
  },
  {
    value: "DataConnectionStatusChanged",
    label: "Company data connection status has changed",
  },
  {
    value: "Dataset data changed",
    label: "Dataset data changed",
  },
  {
    value: "Data Sync Status Changed To Error",
    label: "Dataset status has changed to An Error State",
  },
  {
    value: "Push Operation Status Changed()",
    label: "Push operation status has changed",
  },
  {
    value: "Push Operation Timed Out",
    label: "Push operation has timed out",
  },
  {
    value: "account-categories-updated",
    label: "Account categories updated",
  },
  {
    value: "sync-connection-deleted",
    label: "Sync connection deleted",
  },
  {
    value: "sync-complete",
    label: "Expenses sync completed",
  },
  {
    value: "sync-failed",
    label: "Expenses sync failed",
  },
  {
    value: "Rate Limit Reached",
    label: "Client rate limit reached",
  },
  {
    value: "Rate Limit Reset",
    label: "Client rate limit reset",
  },
];

interface FormData {
  ruleType: { value: RuleType; label: string };
  company: { value: string; label: string };
  emails: string;
  webhookUrl: string;
}

const CreateRuleModal: React.FC<CreateRuleModalProps> = ({
  onSuccess,
  onClose,
  companies,
}) => {
  const { addToast } = useToast();
  const {
    watch,
    setValue,
    register,
    handleSubmit,
    control,
    trigger,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: "onBlur",
  });
  const [{ loading }, createRule] = useCreateRuleMutation();
  const selectedRuleType = watch("ruleType");

  const onSubmit = async (formData: FormData) => {
    const { company, ruleType, emails, webhookUrl } = formData;
    const data = {
      companyId: company?.value ? company.value : undefined,
      type: ruleType?.value,
      notifiers: {
        emails: formatEmails(emails),
        webhook: webhookUrl ? webhookUrl : undefined,
      },
    };
    try {
      await createRule({ data });
      addToast({
        variant: "success",
        message: "New rule created",
      });
      onSuccess();
    } catch (error) {
      addToast({
        variant: "error",
        message: "Failed to create new rule",
      });
    }
  };

  const companySelect = (
    field: ControllerRenderProps<FormData, "company">,
    fieldState: ControllerFieldState
  ): React.ReactElement => (
    <SelectInput
      label="Company"
      showLabel
      inputName="Company"
      id="company"
      data-testid="company-input"
      isSkeleton={!companies}
      placeholder="Select..."
      error={!!fieldState?.error?.message}
      errorMessage={fieldState?.error?.message}
      items={
        companies?.results
          ? [
              { value: "", label: "All companies" },
              ...companies.results.map((c) => ({
                value: c.id,
                label: c.name,
              })),
            ]
          : []
      }
      handleSelectedItemChange={(...args) => {
        field.onChange(...args);
        trigger("company");
      }}
      disabled={clientRuleTypes.includes(selectedRuleType?.value)}
      ref={field.ref}
    />
  );

  const clientRuleTypeInfo = (
    <div
      className={`${styles.warningBannerContainer} ${styles.clientRuleTypeInfo}`}
    >
      <InfoBanner
        data-testid="client-rule-type-info"
        icon={<CircleEmptyAlertIcon />}
        title=""
        subtitle="This rule applies to a client as a whole, so cannot be filtered by company."
        color="neutral"
      />
    </div>
  );

  return (
    <Modal onClose={onClose}>
      <ModalBody
        size="medium"
        title="Create new rule"
        subtitle="Create a rule, set notification types and track specific companies"
        className={styles.formModal}
        data-testid="create-rule-modal"
        onClose={onClose}
      >
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <Controller
            name="ruleType"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <SelectInput
                label="Rule type"
                showLabel
                inputName="Rule Type"
                id="rule-type"
                data-testid="rule-type-input"
                placeholder="Select..."
                errorMessage={fieldState?.error?.message}
                error={!!fieldState?.error?.message}
                items={ruleTypeOptions}
                handleSelectedItemChange={(...args) => {
                  field.onChange(...args);
                  trigger("ruleType");
                  if (args[0] && clientRuleTypes.includes(args[0].value)) {
                    setValue("company", { value: "", label: "All companies" });
                  }
                }}
                ref={field.ref}
                data-autofocus
              />
            )}
          />
          <Controller
            name="company"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) =>
              clientRuleTypes.includes(selectedRuleType?.value)
                ? clientRuleTypeInfo
                : companySelect(field, fieldState)
            }
          />
          <TextArea
            className={styles.textarea}
            label="Email addresses to notify"
            id="emails-to-notify"
            inputName="Notification Emails"
            description="A comma-separated list of emails to notify when an alert for this rule is triggered"
            error={!!errors?.emails}
            errorMessage={errors?.emails?.message}
            {...register("emails", {
              validate: validateEmails,
            })}
          />
          <TextInput
            label="Webhook notification URL"
            inputName="Webhook notification URL"
            id="webhook-notification-url"
            placeholder="https://www.website.com"
            error={!!errors?.webhookUrl}
            errorMessage={errors?.webhookUrl?.message}
            {...register("webhookUrl", {
              validate: validateWebhookUrl,
            })}
          />
          <div className={styles.footerContainer}>
            <Button
              data-testid="cancel-button"
              label="Cancel"
              buttonName="Cancel"
              variant="secondary"
              color="interface"
              onClick={onClose}
            />
            <Button
              buttonName="Save"
              label="Save changes"
              isLoading={loading}
              disabled={!isValid}
            />
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default CreateRuleModal;
